import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { interval } from 'd3-timer';
import useIsTrackEnabled from '../../../src/hooks/useIsTrackEnabled/useIsTrackEnabled';
import { Icon } from '@alucio/lux-ui';
import { AttendeesOrderContext, ActionType, canUpdateDominantSpeaker, } from '../AlucioVideoProvider/AttendeesOrderProvider/AttendeesOrder';
let clipId = 0;
const getUniqueClipId = () => clipId++;
// @ts-ignore
const AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext;
export function initializeAnalyser(stream) {
    audioContext = audioContext || new AudioContext();
    const audioSource = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.smoothingTimeConstant = 0.4;
    analyser.fftSize = 512;
    audioSource.connect(analyser);
    return analyser;
}
function AudioLevelIndicator({ size, audioTrack, background, isPhoneAudio, participant, color = 'rgba(255, 255, 255, 1.0)', }) {
    // The SVG path of the phone icon is smaller than that of the mic
    const SIZE = size || 18;
    const ref = useRef(null);
    const mediaStreamRef = useRef();
    const isTrackEnabled = useIsTrackEnabled(audioTrack);
    const { state: attendeesOrderState, dispatch } = useContext(AttendeesOrderContext);
    // WE KEEP A REF OF THE ATTENDEES ORDER STATE
    const referenceLatestOrderState = useRef(attendeesOrderState);
    const participantId = useMemo(() => participant === null || participant === void 0 ? void 0 : participant.identity.split('.')[0], [participant]);
    /* eslint-disable max-len */
    const phoneSVGPath = 'm6.29856,10.22672c1.52082,2.98882 3.971,5.43897 6.95982,6.95978l2.32351,-2.32342c0.29567,-0.29567 0.70756,-0.3802 1.07725,-0.26399c1.18282,0.39072 2.45015,0.60199 3.77034,0.60199c0.28008,0 0.54863,0.11127 0.74672,0.30923c0.19808,0.19808 0.30936,0.46676 0.30936,0.74684l0,3.69646c0,0.28008 -0.11127,0.54863 -0.30936,0.74672c-0.19808,0.19808 -0.46664,0.30936 -0.74672,0.30936c-4.76179,0 -9.32842,-1.89164 -12.69546,-5.25859c-3.36703,-3.36707 -5.25862,-7.93373 -5.25862,-12.69544c0,-0.28009 0.11127,-0.54872 0.30933,-0.74678c0.19806,-0.19806 0.46668,-0.30933 0.74678,-0.30933l3.69643,0c0.28009,0 0.54874,0.11127 0.74679,0.30933c0.19806,0.19806 0.30932,0.46668 0.30932,0.74678c0,1.32015 0.21123,2.58749 0.60199,3.77034c0.11618,0.36964 0.03169,0.78154 -0.26402,1.07725l-2.32346,2.32347z';
    /* eslint-disable max-len */
    const micSVGPath = 'M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z';
    const SVGPath = isPhoneAudio ? phoneSVGPath : micSVGPath;
    useEffect(() => {
        referenceLatestOrderState.current = attendeesOrderState;
    }, [attendeesOrderState]);
    useEffect(() => {
        // Here we listen for the 'stopped' event on the audioTrack. When the audioTrack is stopped,
        // we stop the cloned track that is stored in mediaStreamRef. It is important that we stop
        // all tracks when they are not in use. Browsers like Firefox don't let you create a stream
        // from a new audio device while the active audio device still has active tracks.
        const handleStopped = () => { var _a; return (_a = mediaStreamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach(track => track.stop()); };
        audioTrack === null || audioTrack === void 0 ? void 0 : audioTrack.on('stopped', handleStopped);
        return () => {
            audioTrack === null || audioTrack === void 0 ? void 0 : audioTrack.off('stopped', handleStopped);
        };
    }, [audioTrack]);
    useEffect(() => {
        const SVGClipElement = ref.current;
        if (audioTrack && isTrackEnabled && SVGClipElement) {
            // Here we create a new MediaStream from a clone of the mediaStreamTrack.
            // A clone is created to allow multiple instances of this component for a single
            // AudioTrack on iOS Safari. It is stored in a ref so that the cloned track can be stopped
            // when the original track is stopped.
            mediaStreamRef.current = new MediaStream([audioTrack.mediaStreamTrack.clone()]);
            let analyser = initializeAnalyser(mediaStreamRef.current);
            const reinitializeAnalyser = () => {
                analyser = initializeAnalyser(mediaStreamRef.current);
            };
            // Here we reinitialize the AnalyserNode on focus to avoid an issue in Safari
            // where the analysers stop functioning when the user switches to a new tab
            // and switches back to the app.
            window.addEventListener('focus', reinitializeAnalyser);
            const sampleArray = new Uint8Array(analyser.frequencyBinCount);
            const timer = interval(() => {
                analyser.getByteFrequencyData(sampleArray);
                let values = 0;
                const length = sampleArray.length;
                for (let i = 0; i < length; i++) {
                    values += sampleArray[i];
                }
                const volume = Math.min(21, Math.max(0, Math.log10(values / length / 3) * 14));
                // WE'LL UPDATE THE ATTENDEES ORDER STATE WITH THE NEW DOMINANT SPEAKER ONLY IF:
                // THE USER IS IN THE FIRST PAGE, THE NEW DOMINANT IS DIFFERENT THAN THE LATEST AND
                // THERE HAS PASSED AT LEAST 2 SECONDS SINCE THE LAST ONE WAS SET
                const canUpdate = participant && canUpdateDominantSpeaker(referenceLatestOrderState.current, volume, participantId);
                if (canUpdate) {
                    dispatch({ type: ActionType.NOTIFY_PARTICIPANT_SPEAKING, value: participantId });
                }
                SVGClipElement === null || SVGClipElement === void 0 ? void 0 : SVGClipElement.setAttribute('y', String(21 - volume));
            }, 50);
            return () => {
                SVGClipElement.setAttribute('y', '21');
                timer.stop();
                window.removeEventListener('focus', reinitializeAnalyser);
            };
        }
    }, [audioTrack, isTrackEnabled]);
    // Each instance of this component will need a unique HTML ID
    const clipPathId = `audio-level-clip-${getUniqueClipId()}`;
    return isTrackEnabled ? (React.createElement("svg", { focusable: "false", viewBox: "0 0 24 24", "aria-hidden": "true", height: `${SIZE}px`, width: `${SIZE}px` },
        React.createElement("defs", null,
            React.createElement("clipPath", { id: clipPathId },
                React.createElement("rect", { ref: ref, x: "0", y: "21", width: "24", height: "24" }))),
        React.createElement("path", { fill: background || 'rgba(255, 255, 255, 1.0 )', d: SVGPath }),
        React.createElement("path", { fill: "#0c0", clipPath: `url(#${clipPathId})`, d: SVGPath }))) : (
    // The Microphone Off Icon is slightly bigger than the SVG icon
    React.createElement(Icon, { name: "microphone-off", style: { width: SIZE, height: SIZE, paddingBottom: 1, color: color } }));
}
export default React.memo(AudioLevelIndicator);
//# sourceMappingURL=AudioLevelIndicator.js.map